import React from "react";
import { useParams, Link } from "react-router-dom";
import { Card, Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import { projectList, categoryInfo } from "../data/project-list";

const cardVariants = {
    offscreen: { opacity: 0, y: 30 },
    onscreen: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", bounce: 0.2, duration: 0.6 }
    }
};

const ProjectList = ({ darkMode }) => {
    const { project_type } = useParams();
    const projects = projectList[project_type] || [];
    const category = categoryInfo[project_type];

    const bgColor = darkMode ? "#212529" : "#f8f9fa";
    const textColor = darkMode ? "#f8f9fa" : "#212529";
    const accentColor = category?.color || "#2E7D32";

    return (
        <Container style={{ minHeight: "100vh", padding: "30px 15px", backgroundColor: bgColor, color: textColor }}>
            {category && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-4"
                >
                    <div style={{
                        borderRadius: '12px',
                        overflow: 'hidden',
                        position: 'relative',
                        marginBottom: '20px'
                    }}>
                        <img
                            src={category.image}
                            alt={category.title}
                            style={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain',
                                display: 'block',
                                filter: 'brightness(0.6)'
                            }}
                        />
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: '30px',
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))'
                        }}>
                            <h2 style={{ color: '#fff', fontWeight: 'bold', margin: 0 }}>
                                {category.title}
                            </h2>
                        </div>
                    </div>
                    <p style={{
                        fontSize: '1.05rem',
                        lineHeight: '1.7',
                        maxWidth: '900px',
                        margin: '0 auto 10px',
                        textAlign: 'center',
                        color: darkMode ? '#ccc' : '#555'
                    }}>
                        {category.description}
                    </p>
                </motion.div>
            )}

            <hr style={{ borderColor: darkMode ? '#495057' : '#dee2e6', margin: '20px 0 30px' }} />

            <h4 className="mb-4" style={{ color: darkMode ? '#A5D6A7' : accentColor, fontWeight: '600' }}>
                Projects ({projects.length})
            </h4>

            {projects.length > 0 ? (
                projects.map((project, index) => (
                    <motion.div
                        key={project.id}
                        initial="offscreen"
                        whileInView="onscreen"
                        viewport={{ once: true, amount: 0.2 }}
                        variants={cardVariants}
                        className="mb-4"
                    >
                        <Card style={{
                            backgroundColor: darkMode ? '#2c3034' : '#fff',
                            color: textColor,
                            border: darkMode ? '1px solid #495057' : '1px solid #e0e0e0',
                            borderRadius: '10px',
                            overflow: 'hidden'
                        }} className="shadow-sm">
                            <Row className="g-0 align-items-stretch">
                                <Col md={4}>
                                    <img
                                        src={project.image}
                                        alt={project.name}
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            display: 'block',
                                            padding: '8px'
                                        }}
                                    />
                                </Col>
                                <Col md={8} className="d-flex flex-column justify-content-center">
                                    <Card.Body style={{ padding: '24px' }}>
                                        <div className="d-flex align-items-center mb-2">
                                            <span style={{
                                                display: 'inline-block',
                                                width: '4px',
                                                height: '24px',
                                                backgroundColor: accentColor,
                                                borderRadius: '2px',
                                                marginRight: '12px'
                                            }}></span>
                                            <Card.Title style={{
                                                fontSize: '1.15rem',
                                                fontWeight: '600',
                                                margin: 0,
                                                color: darkMode ? '#f8f9fa' : '#212529'
                                            }}>
                                                {project.name}
                                            </Card.Title>
                                        </div>
                                        <p style={{
                                            fontSize: '0.85rem',
                                            color: accentColor,
                                            fontWeight: '500',
                                            marginBottom: '10px'
                                        }}>
                                            {project.duration}
                                        </p>
                                        <Card.Text style={{
                                            fontSize: '0.95rem',
                                            lineHeight: '1.6',
                                            color: darkMode ? '#ccc' : '#555'
                                        }}>
                                            {project.brief}
                                        </Card.Text>
                                        <Link
                                            to={`/project-details/${project.id}`}
                                            className="btn btn-sm mt-1"
                                            style={{
                                                backgroundColor: accentColor,
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '6px 18px',
                                                fontWeight: '500'
                                            }}
                                        >
                                            View Details
                                        </Link>
                                    </Card.Body>
                                </Col>
                            </Row>
                        </Card>
                    </motion.div>
                ))
            ) : (
                <div className="text-center py-5">
                    <p style={{ fontSize: '1.1rem', color: darkMode ? '#aaa' : '#777' }}>
                        No projects available for this category.
                    </p>
                </div>
            )}

        </Container>
    );
};

export default ProjectList;
